import { createListenerMiddleware } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';
import { logger } from 'datadog-logs';
import { MediaTrackState } from 'domain/Broadcast';
import { addAppConfirmationDialog } from 'modules/alerts/state';
import { RootState } from 'store';
import { agoraActions, selectAudioTrackState } from 'store/features/agora';
import { userMuted_WS } from 'store/features/event';

import { userActions } from './actions';
import {
  setAudioEnabled,
  setCameraPermissionState,
  setMediaSettings,
  setMicrophoneIssueAcknowledged,
  setMicrophonePermissionState,
} from './userSlice.actions';
import { getDeviceLocalStorageKey, getInitialDeviceId } from './utils';

type PermissionStatePayload = 'granted' | 'denied' | 'system_denied' | 'prompt';

export const userListener = createListenerMiddleware();

userListener.startListening({
  actionCreator: userMuted_WS,
  effect: ({ payload: { mutedByName } }, { dispatch }) => {
    dispatch(setAudioEnabled(false));
    dispatch(
      addAppConfirmationDialog({
        title: `You have been muted by ${mutedByName}`,
        description: 'You can unmute yourself by clicking the microphone below.',
        variant: 'confirmation',
        confirmLabel: 'Got it',
      })
    );
  },
});

userListener.startListening({
  actionCreator: userActions.mediaSettings.settingsUpdated,
  effect: (
    { payload: { blurLevel, audioInputDeviceId, audioOutputDeviceId, videoDeviceId } },
    { getOriginalState }
  ) => {
    const originalState = getOriginalState() as RootState;
    const previousSettings = originalState.user.mediaSettings;

    // Only store as "selected" if the device actually changed from previous value
    // This prevents marking all devices as user-selected when only one device is changed
    if (previousSettings.audioInputDeviceId !== audioInputDeviceId) {
      localStorage.setItem(getDeviceLocalStorageKey('audioinput', 'selected'), audioInputDeviceId);
    }
    if (previousSettings.audioOutputDeviceId !== audioOutputDeviceId) {
      localStorage.setItem(getDeviceLocalStorageKey('audiooutput', 'selected'), audioOutputDeviceId);
    }
    if (previousSettings.videoDeviceId !== videoDeviceId) {
      localStorage.setItem(getDeviceLocalStorageKey('videoinput', 'selected'), videoDeviceId);
    }

    localStorage.setItem('blurLevel', blurLevel.toString());
  },
});

userListener.startListening({
  actionCreator: userActions.navigator.mediaDevices.devicesFound,
  effect: ({ payload: devices }, { dispatch, getOriginalState }) => {
    const originalState = getOriginalState() as RootState;
    const originalDevices = originalState.user.mediaDevices;
    const isInitialLoad = originalDevices.length === 0 && devices.length > 0;

    // Set initial device IDs
    const audioInputResult = getInitialDeviceId(devices, 'audioinput');
    const audioOutputResult = getInitialDeviceId(devices, 'audiooutput');
    const videoResult = getInitialDeviceId(devices, 'videoinput');

    dispatch(
      setMediaSettings({
        audioInputDeviceId: audioInputResult.deviceId,
        audioOutputDeviceId: audioOutputResult.deviceId,
        videoDeviceId: videoResult.deviceId,
      })
    );

    // Track device selection telemetry
    monitoring.trackAction('devices.device_selection', {
      audioInput: audioInputResult,
      audioOutput: audioOutputResult,
      video: videoResult,
    });

    // Track not_found signals (only on initial load or when device disappears)
    const hasNoMicrophone = !devices.some((d) => d.kind === 'audioinput');
    const hasNoCamera = !devices.some((d) => d.kind === 'videoinput');
    const hasNoSpeaker = !devices.some((d) => d.kind === 'audiooutput');

    if (hasNoMicrophone) {
      monitoring.trackAction('devices.microphone.not_found');
    }
    if (hasNoCamera) {
      monitoring.trackAction('devices.camera.not_found');
    }
    if (hasNoSpeaker) {
      monitoring.trackAction('devices.speaker.not_found');
    }

    // Track not_found_recovered (device plugged in after being missing)
    const hadNoMicrophone = originalDevices.length > 0 && !originalDevices.some((d) => d.kind === 'audioinput');
    if (hadNoMicrophone && !hasNoMicrophone) {
      monitoring.trackAction('devices.microphone.not_found_recovered');
    }

    const hadNoCamera = originalDevices.length > 0 && !originalDevices.some((d) => d.kind === 'videoinput');
    if (hadNoCamera && !hasNoCamera) {
      monitoring.trackAction('devices.camera.not_found_recovered');
    }

    const hadNoSpeaker = originalDevices.length > 0 && !originalDevices.some((d) => d.kind === 'audiooutput');
    if (hadNoSpeaker && !hasNoSpeaker) {
      monitoring.trackAction('devices.speaker.not_found_recovered');
    }

    // Log device info
    logger.info('[MediaDevices] devices found', {
      mediaDevices: {
        devices,
        deviceCount: devices.length,
        audioInputCount: devices.filter((d) => d.kind === 'audioinput').length,
        audioOutputCount: devices.filter((d) => d.kind === 'audiooutput').length,
        videoInputCount: devices.filter((d) => d.kind === 'videoinput').length,
        isInitialLoad,
      },
    });
  },
});

userListener.startListening({
  actionCreator: agoraActions.audioTrack.toggling.completed,
  effect: (_, { dispatch, getState }) => {
    const audioTrackState = selectAudioTrackState(getState() as RootState);
    if (audioTrackState === MediaTrackState.Disabled) {
      dispatch(setMicrophoneIssueAcknowledged(false));
    }
  },
});

// Track permission granted transitions
userListener.startListening({
  actionCreator: setMicrophonePermissionState,
  effect: ({ payload }: { payload: PermissionStatePayload }, { getOriginalState }) => {
    const originalState = getOriginalState() as RootState;
    if (payload === 'granted' && originalState.user.microphonePermissionState !== 'granted') {
      monitoring.trackAction('devices.microphone.permission_granted');
    }
  },
});

userListener.startListening({
  actionCreator: setCameraPermissionState,
  effect: ({ payload }: { payload: PermissionStatePayload }, { getOriginalState }) => {
    const originalState = getOriginalState() as RootState;
    if (payload === 'granted' && originalState.user.cameraPermissionState !== 'granted') {
      monitoring.trackAction('devices.camera.permission_granted');
    }
  },
});
